import type { AgentUsageType } from "@app/types/assistant/agent";
import { pluralize } from "@app/types/shared/utils/string_utils";
import i18n from "i18next";
import type { ReactNode } from "react";
import { Fragment } from "react";
import { Trans } from "react-i18next";

export function assistantUsageMessage<T extends boolean>({
  assistantName,
  usage,
  isLoading,
  isError,
  shortVersion,
  boldVersion,
  asString,
}: {
  assistantName: string | null;
  usage: AgentUsageType | null;
  isLoading: boolean;
  isError: boolean;
  shortVersion?: boolean;
  boldVersion?: boolean;
  asString?: T;
}): T extends true ? string : ReactNode {
  if (isError) {
    return i18n.t(
      "assistant.usageErrorLoading",
      "Error loading usage data."
    ) as T extends true ? string : ReactNode;
  }

  if (isLoading) {
    return i18n.t(
      "assistant.usageLoading",
      "Loading usage data..."
    ) as T extends true ? string : ReactNode;
  }

  if (usage) {
    const days = usage.timePeriodSec / (60 * 60 * 24);
    const nb = usage.messageCount || 0;

    if (shortVersion) {
      return (
        asString ? (
          i18n.t(
            "assistant.usageMessagesOverDaysAsString",
            "{{count}} message{{plural}} over the last {{days}} days",
            { count: nb, plural: pluralize(nb), days }
          )
        ) : (
          <Trans
            i18nKey="assistant.usageMessagesOverDaysJsx"
            defaults="<0>{{count}} message{{plural}}</0> over the last {{days}} days"
            values={{ count: nb, plural: pluralize(nb), days }}
            components={[
              boldVersion ? (
                <span key="0" className="font-bold" />
              ) : (
                <Fragment key="0" />
              ),
            ]}
          />
        )
      ) as T extends true ? string : ReactNode;
    }

    return (
      asString ? (
        i18n.t(
          "assistant.usageAgentUsedTimes",
          "{{agent}} has been used {{count}} time(s) in the last {{days}} days.",
          {
            agent: assistantName ? "@" + assistantName : "This agent",
            count: nb,
            days: usage.timePeriodSec / (60 * 60 * 24),
          }
        )
      ) : (
        <Trans
          i18nKey="assistant.usageAgentUsedTimesJsx"
          defaults="{{agent}} has been used <0>{{count}} time{{plural}}</0> in the last {{days}} days."
          values={{
            agent: assistantName ? "@" + assistantName : "This agent",
            count: nb,
            plural: pluralize(nb),
            days: usage.timePeriodSec / (60 * 60 * 24),
          }}
          components={[
            boldVersion ? (
              <span key="0" className="font-bold" />
            ) : (
              <Fragment key="0" />
            ),
          ]}
        />
      )
    ) as T extends true ? string : ReactNode;
  }

  return "" as T extends true ? string : ReactNode;
}

export function assistantActiveUsersMessage<T extends boolean>({
  usage,
  isLoading,
  isError,
  asString,
}: {
  usage: AgentUsageType | null;
  isLoading: boolean;
  isError: boolean;
  asString?: T;
}): T extends true ? string : ReactNode {
  if (isError) {
    return i18n.t(
      "assistant.usageErrorLoading",
      "Error loading usage data."
    ) as T extends true ? string : ReactNode;
  }

  if (isLoading) {
    return i18n.t(
      "assistant.usageLoading",
      "Loading usage data..."
    ) as T extends true ? string : ReactNode;
  }

  if (usage) {
    const days = usage.timePeriodSec / (60 * 60 * 24);
    const nb = usage.userCount || 0;

    return i18n.t(
      "assistant.usageActiveUsersOverDays",
      "{{count}} active user{{plural}} over the last {{days}} days",
      { count: nb, plural: pluralize(nb), days }
    ) as T extends true ? string : ReactNode;
  }

  return "" as T extends true ? string : ReactNode;
}
